'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Search, Plus, Check, Square, X, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from "@/components/ui/input";
import { DisplayProduct } from '../types';
import Image from 'next/image';
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { groupVendorProductsByCoursePriority, groupMenuItemsHierarchically } from '@/app/products/utils';
import { DisplayVendorProduct } from '@/app/products/types';
import { VendorCourse, VendorSubcategory } from '@/hooks/useMenuWebsiteApi';

interface ProductSearchProps {
  products: DisplayProduct[];
  onAddToCart: (product: DisplayProduct) => void;
  selectedProducts?: Set<string>;
  courses?: VendorCourse[];
  subcategories?: VendorSubcategory[];
}

// Skeleton component for product loading
const ProductSkeleton = () => (
  <Card className="border-b border-x-0 border-t-0 rounded-none shadow-none bg-transparent last:border-b-0">
    <CardContent className="p-4">
      <div className="flex gap-4">
        <Skeleton className="w-24 h-24 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex gap-1.5 mb-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-4" />
          </div>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <Skeleton className="h-5 w-3/4 mb-2" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-2/3 mb-3" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export function ProductSearch({ 
  products, 
  onAddToCart, 
  selectedProducts = new Set(),
  courses = [],
  subcategories = []
}: ProductSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [loadedCourses, setLoadedCourses] = useState<Set<string>>(new Set());
  const [openCourses, setOpenCourses] = useState<string[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);


  const filteredAndGroupedProducts = useMemo(() => {
    let filtered = [...products];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.food_course?.toLowerCase().includes(searchLower)
      );
    }

    // Use hierarchical grouping when subcategories are available, otherwise use course grouping
    let allSortedCourses: [string, DisplayProduct[]][];
    let hierarchicalData: [string, { items: DisplayProduct[]; subcategories: [string, DisplayProduct[]][] }][] | null = null;
    
    if (courses && courses.length > 0) {
      // Check if we should use hierarchical grouping
      if (subcategories && subcategories.length > 0) {
        // Convert DisplayProduct to DisplayMenuItem format for hierarchical grouping
        const menuItemsFormat = filtered.map(product => ({
          ...product,
          subcategory_id: product.subcategory_id || null
        }));
        
        // Convert API courses to Database-compatible format
        const dbCourses = courses.map(course => ({
          ...course,
          vendor_id: course.vendor_id || ''
        }));
        
        // Convert API subcategories to the expected format
        const dbSubcategories = subcategories.map(sub => ({
          ...sub,
          vendor_id: '' // Not needed for grouping function
        }));
        
        // Get hierarchical grouping
        hierarchicalData = groupMenuItemsHierarchically(menuItemsFormat, dbCourses, dbSubcategories);
        
        // For backward compatibility, flatten the hierarchical data for the accordion structure
        allSortedCourses = hierarchicalData.map(([courseName, courseData]) => {
          const allCourseItems = [
            ...courseData.items,
            ...courseData.subcategories.flatMap(([, subItems]) => subItems)
          ];
          return [courseName, allCourseItems] as [string, DisplayProduct[]];
        });
      } else {
        // Fall back to flat course grouping
        // Convert DisplayProduct to DisplayVendorProduct format temporarily for grouping
        const vendorProductsFormat: DisplayVendorProduct[] = filtered.map(product => ({
          id: product.id,
          name: product.name,
          description: product.description || undefined,
          image_url: product.image_url || undefined,
          food_course: product.food_course || undefined,
          subcategory_id: product.subcategory_id || undefined,
          is_vegetarian: product.is_vegetarian || false,
          contains_egg: product.contains_egg || false,
          not_a_food: product.not_a_food || undefined,
          vendor_id: undefined
        }));
        
        // Convert API courses to Database-compatible format for the utility function
        const dbCourses = courses.map(course => ({
          ...course,
          vendor_id: course.vendor_id || ''
        }));
        
        // Get the grouped structure but map back to original products
        const groupedByPriority = groupVendorProductsByCoursePriority(vendorProductsFormat, dbCourses);
        allSortedCourses = groupedByPriority.map(([courseName, vendorProducts]) => {
          // Map back to original DisplayProduct format by finding matching products
          const originalProducts = vendorProducts.map(vp => 
            filtered.find(p => p.id === vp.id)!
          ).filter(Boolean);
          return [courseName, originalProducts] as [string, DisplayProduct[]];
        });
      }
    } else {
      // Fallback: Group by food_course values but try to clean up UUID display
      const grouped = filtered.reduce((acc: Record<string, DisplayProduct[]>, product) => {
        let course = product.food_course || 'Other';
        
        // If it looks like a UUID, try to make it more readable
        if (course && course.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          course = 'Unknown Course';
        }
        
        // Clean up common course names
        if (course === 'Not food' || course === 'not_a_food') {
          course = 'Other Items';
        }
        
        if (!acc[course]) {
          acc[course] = [];
        }
        acc[course].push(product);
        return acc;
      }, {});
      
      // Sort alphabetically when no course priority is available
      allSortedCourses = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    }

    // Initialize first course as loaded if not already set
    if (allSortedCourses.length > 0 && loadedCourses.size === 0 && openCourses.length === 0) {
      const firstCourse = allSortedCourses[0][0];
      setLoadedCourses(new Set([firstCourse]));
      setOpenCourses([firstCourse]);
    }

    // Only include products for loaded courses
    const loadedCoursesData = allSortedCourses.map(([course, courseProducts]) => {
      if (loadedCourses.has(course)) {
        return [course, courseProducts] as [string, DisplayProduct[]];
      } else {
        return [course, []] as [string, DisplayProduct[]]; // Empty array for unloaded courses
      }
    });

    return { 
      sortedCourses: loadedCoursesData, 
      allCourses: allSortedCourses,
      hierarchicalData,
      totalFiltered: filtered.length 
    };
  }, [products, searchTerm, loadedCourses, openCourses, courses, subcategories]);

  // Reset state when search term changes
  useEffect(() => {
    setLoadedCourses(new Set());
    setOpenCourses([]);
  }, [searchTerm]);

  // Handle course accordion changes
  const handleCourseToggle = (course: string) => {
    if (!loadedCourses.has(course)) {
      // Load products for this course
      setLoadedCourses(prev => new Set(Array.from(prev).concat(course)));
    }
  };

  // Handle search mode transitions
  const handleEnterSearchMode = () => {
    setIsSearchMode(true);
  };

  const handleExitSearchMode = () => {
    setIsSearchMode(false);
    setSearchTerm(''); // Clear search when hitting back button
  };

  // Render function for hierarchical course structure
  const renderHierarchicalCourse = (courseName: string, courseData: { items: DisplayProduct[]; subcategories: [string, DisplayProduct[]][] }) => {
    const hasItems = courseData.items.length > 0;
    const hasSubcategories = courseData.subcategories.length > 0;
    
    if (!hasItems && !hasSubcategories) return null;

    return (
      <div key={courseName} className="space-y-0">
        {/* Items without subcategories */}
        {hasItems && courseData.items.map((product) => (
          product.type === 'combo' ? (
            <ComboCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
          ) : (
            <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
          )
        ))}
        
        {/* Subcategories with their items */}
        {hasSubcategories && courseData.subcategories.map(([subcategoryName, subcategoryItems]) => (
          <div key={subcategoryName} className="pl-4 border-l-2 border-gray-200 ml-2 my-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 px-2">{subcategoryName}</h4>
            <div className="space-y-0">
              {subcategoryItems.map((product) => (
                product.type === 'combo' ? (
                  <ComboCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                ) : (
                  <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Reusable component for combo cards
  const ComboCard = ({ product, onAddToCart, selectedProducts }: {
    product: DisplayProduct;
    onAddToCart: (product: DisplayProduct) => void;
    selectedProducts: Set<string>;
  }) => (
    <Card
      className={`group transition-all border-b border-x-0 border-t-0 rounded-none shadow-none duration-200 cursor-pointer bg-transparent last:border-b-0 `}
      onClick={() => onAddToCart(product)}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {product.image_url && (
            <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={product.image_url}
                alt={product.name}
                fill
                className="object-cover transition-transform rounded-xl"
              />
            </div>
          )}

          <div className="flex-1 min-w-0 border px-3 pt-3 rounded-lg border-gray-200">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-md capitalize truncate">{product.name}</h3>
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">COMBO</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                {selectedProducts.has(product.id) ? (
                  <Button variant="green" size="sm">
                    <Check className="h-3 w-3 mr-1" /> ADDED
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="hover:bg-primary/10 hover:text-primary">
                    <Plus className="h-3 w-3 mr-1" /> ADD
                  </Button>
                )}
              </div>
            </div>

            {product.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1 mb-3">
                {product.description}
              </p>
            )}

            {/* Combo groups display */}
            {product.combo_groups && product.combo_groups.length > 0 && (
              <div className="space-y-2 mb-3 ">
                {product.combo_groups
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map((group) => (
                  <div key={group.id} className="bg-gray-50 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium text-gray-900">{group.name}</h4>
                      {group.is_optional && (
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">Optional</span>
                      )}
                    </div>
                    {group.combo_group_products && group.combo_group_products.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {group.combo_group_products.map((cgp) => (
                          cgp.vendor_products ? (
                            <div key={cgp.id} className="flex items-center gap-2 bg-white p-2 rounded-lg border shadow-sm">
                              {cgp.vendor_products.image_url && (
                                <div className="relative w-8 h-8 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                                  <Image
                                    src={cgp.vendor_products.image_url}
                                    alt={cgp.vendor_products.name}
                                    fill
                                    className="object-cover"
                                  />
                                </div>
                              )}
                              <span className="text-xs text-gray-700 font-medium">
                                {cgp.vendor_products.name}
                              </span>
                            </div>
                          ) : null
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Reusable component for product cards
  const ProductCard = ({ product, onAddToCart, selectedProducts }: {
    product: DisplayProduct;
    onAddToCart: (product: DisplayProduct) => void;
    selectedProducts: Set<string>;
  }) => (
    <Card
      className={`group transition-all border-b border-x-0 border-t-0 rounded-none shadow-none duration-200 cursor-pointer bg-transparent last:border-b-0 `}
      onClick={() => onAddToCart(product)}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {product.image_url && (
            <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={product.image_url}
                alt={product.name}
                fill
                className="object-cover transition-transform rounded-xl"
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
          <div className="flex gap-1.5">
                {!product.not_a_food && (
                  <>
                    {product.is_vegetarian === true ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="relative h-4 w-4">
                            <Square className="h-4 w-4 text-green-600 fill-white stroke-2" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-1.5 w-1.5 bg-green-600 rounded-full"></div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Vegetarian</TooltipContent>
                      </Tooltip>
                    ) : product.is_vegetarian === false ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="relative h-4 w-4">
                            <Square className="h-4 w-4 text-red-600 fill-white stroke-2" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-1.5 w-1.5 bg-red-600 rounded-full"></div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Non-vegetarian</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {product.contains_egg === true && (
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="relative h-4 w-4">
                            <Square className="h-4 w-4 text-amber-500 fill-white stroke-2" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-1.5 w-1.5 bg-amber-500 rounded-full"></div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Contains Egg</TooltipContent>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-md capitalize truncate">{product.name}</h3>
              </div>
            </div>

            {product.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {product.description}
              </p>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between">
              {selectedProducts.has(product.id) ? (
                <Button variant="green" size="sm">
                  <Check className="h-3 w-3 mr-1" /> ADDED
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="hover:bg-primary/10 hover:text-primary">
                  <Plus className="h-3 w-3 mr-1" /> ADD
                </Button>
              )}
            </div>
        </div>
      </CardContent>
    </Card>
  );

  // Normal mode search bar component
  const NormalSearchBar = () => (
    <div className="sticky top-0 z-10 bg-muted pt-4 pb-2 space-y-4 px-2 border-b">
      <div className="flex flex-col gap-2">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, description, or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={handleEnterSearchMode}
            className="pl-9 pr-9 rounded-xl py-6 h-12 text-base text-muted-foreground cursor-pointer"
            readOnly={!isSearchMode}
            role="searchbox"
            aria-label="Search products"
          />
        </div>
      </div>
    </div>
  );

  // Search mode header component (memoized to prevent re-animation on search term changes)
  const SearchModeHeader = React.useMemo(() => (
    <motion.div 
      key="search-header" // Add key to prevent re-animation
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm"
    >
      <div className="flex items-center gap-3 p-4">
        <button
          onClick={handleExitSearchMode}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="relative flex-1">
          <Search className="absolute top-4 left-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, description, or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-9 rounded-xl py-6 h-12 text-base border-0 bg-gray-50 focus-visible:ring-1"
            autoFocus
            role="searchbox"
            aria-label="Search products"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  ), [handleExitSearchMode, searchTerm]); // Only re-create when handleExitSearchMode or searchTerm changes

  return (
    <TooltipProvider>
      <AnimatePresence mode="wait">
        {!isSearchMode ? (
          <motion.div 
            key="normal-mode"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2"
          >
            <NormalSearchBar />
            
            <div className="space-y-4 bg-white px-3">
              {filteredAndGroupedProducts.sortedCourses.length > 0 ? (
                <Accordion 
              type="multiple" 
              value={openCourses}
              onValueChange={setOpenCourses}
              className="space-y-2"
            >
              {filteredAndGroupedProducts.allCourses.map(([course, allCourseProducts]) => {
                const courseProducts = loadedCourses.has(course) ? allCourseProducts : [];
                return (
                <AccordionItem key={course} value={course} data-value={course}>
                  <AccordionTrigger 
                    className="hover:no-underline"
                    onClick={() => handleCourseToggle(course)}
                  >
                    <div className='flex items-center justify-start'>
                      <span className="text-lg font-bold">{course}</span>
                      <span className="ml-1 text-md font-bold">
                        ({allCourseProducts.length})
                      </span>
                      {!loadedCourses.has(course) && openCourses.includes(course) && (
                        <Skeleton className="h-4 w-4 ml-2 rounded-full" />
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1">
                      {loadedCourses.has(course) ? (
                        filteredAndGroupedProducts.hierarchicalData ? 
                          renderHierarchicalCourse(course, filteredAndGroupedProducts.hierarchicalData.find(([c]) => c === course)?.[1] || { items: courseProducts, subcategories: [] })
                        : courseProducts.map((product) => (
                          product.type === 'combo' ? (
                            <ComboCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                          ) : (
                            <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                          )
                        ))
                      ) : (
                        <div className="space-y-0">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <ProductSkeleton key={i} />
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No products found</p>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="search-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white z-40"
          >
            {SearchModeHeader}
            
            {/* Search Results */}
            <motion.div 
              key="search-results-container"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="pt-20 pb-6 px-4 overflow-y-auto h-full"
            >
              {searchTerm ? (
                filteredAndGroupedProducts.totalFiltered > 0 ? (
                  <div className="space-y-4">
                    {filteredAndGroupedProducts.sortedCourses.map(([course, courseProducts]) => {
                      if (courseProducts.length === 0) return null;
                      
                      return (
                        <div key={course}>
                          <h3 className="text-lg font-semibold mb-3 text-gray-800">
                            {course} ({courseProducts.length})
                          </h3>
                          <div className="space-y-0">
                            {courseProducts.map((product) => (
                              product.type === 'combo' ? (
                                <ComboCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                              ) : (
                                <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} selectedProducts={selectedProducts} />
                              )
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No products found</p>
                    <p className="text-gray-400 text-sm mt-1">Try adjusting your search terms</p>
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Start typing to search</p>
                  <p className="text-gray-400 text-sm mt-1">Search by name, description, or course</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </TooltipProvider>
  );
}
